import { useCallback, useMemo, useState } from 'react';

import { IFilterFormItem, TFilterFormValues } from 'common/types';
import { filtersToQueryParams } from 'common/utils/filter';
import {
    skillComplianceFilter,
    skillProgressFilter,
} from 'skill/constants/filter';

interface ISkillCertificateMatrixFilterValues {
    isCompliant?: boolean;
    progress?: [number, number];
}

interface IUseSkillCertificateMatrixFilterOptions {
    hasMandatoryCertificates: boolean;
}

export const useSkillCertificateMatrixFilter = ({
    hasMandatoryCertificates,
}: IUseSkillCertificateMatrixFilterOptions) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [filterValues, setFilterValues] = useState<TFilterFormValues>({});

    const filters = useMemo(
        () =>
            [
                hasMandatoryCertificates
                    ? skillComplianceFilter()
                    : skillProgressFilter(),
            ] as IFilterFormItem[],
        [hasMandatoryCertificates]
    );

    const handleSearch = useCallback((value: string) => {
        setSearchQuery(value);
    }, []);

    const handleSearchClear = useCallback(() => {
        setSearchQuery('');
    }, []);

    const handleFilterChange = useCallback(
        (values?: TFilterFormValues) => {
            if (!values) {
                setFilterValues({});

                return;
            }

            const changedValues: TFilterFormValues = {};

            filters.forEach((filter) => {
                filter.fields.forEach((field) => {
                    const value = values[field.name];

                    if (
                        JSON.stringify(value) !==
                        JSON.stringify(field.initialValue)
                    ) {
                        changedValues[field.name] = value;
                    }
                });
            });

            setFilterValues(changedValues);
        },
        [filters]
    );

    const filterValuesForQuery = useMemo(() => {
        const parsed =
            filtersToQueryParams<ISkillCertificateMatrixFilterValues>(
                filters,
                filterValues
            );

        const { progress, ...rest } = parsed;
        const progressRange = progress as [number, number] | undefined;

        return {
            ...rest,
            progressFrom:
                progressRange && progressRange[0] > 0
                    ? progressRange[0]
                    : undefined,
            progressTo:
                progressRange && progressRange[1] < 1
                    ? progressRange[1]
                    : undefined,
        };
    }, [filters, filterValues]);

    return {
        filters,
        filterValues,
        filterValuesForQuery,
        searchQuery,
        onSearch: handleSearch,
        onSearchClear: handleSearchClear,
        onFilterChange: handleFilterChange,
    };
};
